"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { createContext, createElement, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ComponentPropsWithoutRef, type ComponentType, type CSSProperties, type ElementType, type JSX, type ReactNode, type SVGProps } from "react";
import { createPortal } from "react-dom";
type ModalSizeStyle = Pick<CSSProperties, "width" | "height" | "minWidth" | "minHeight" | "maxWidth" | "maxHeight" | "aspectRatio">;
type OverlayOrigin = {
  left: number;
  top: number;
  width: number;
  height: number;
};
type OverlayItem = {
  id: string;
  render: ReactNode | null;
  onClose: () => void;
  clickToExit: boolean;
};
type OverlayContextValue = {
  overlays: OverlayItem[];
  openFrom: OverlayOrigin | undefined;
  addOverlay: (id: string, render: ReactNode | null, onClose?: () => void, clickToExit?: boolean) => void;
  removeOverlay: (id: string, callClose?: boolean) => void;
  closeTopOverlay: () => void;
};
type OverlaySubContextValue = {
  quit: () => void;
  invalid?: boolean;
};
const OverlayContext = createContext<OverlayContextValue | null>(null);
const OverlaySubContext = createContext<OverlaySubContextValue>({
  quit: () => undefined,
  invalid: true
});
function useOverlayContext() {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error("Overlay components must be rendered inside OverlayProvider.");
  }
  return context;
}
function CloseIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" d="m6.5 6.5 11 11m0-11-11 11" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function OverlayProvider(t0) {
  const $ = _c(32);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [overlays, setOverlays] = useState(t1);
  const [openFrom, setOpenFrom] = useState();
  const pressedBackdropRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const overlaysRef = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const closingRef = useRef(t3);
  const previousOverflowRef = useRef(null);
  let t4;
  let t5;
  if ($[3] !== overlays) {
    t4 = () => {
      overlaysRef.current = overlays;
    };
    t5 = [overlays];
    $[3] = overlays;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (id, t7) => {
      const callClose = t7 === undefined ? true : t7;
      const found = overlaysRef.current.find(overlay => overlay.id === id);
      if (!found) {
        return;
      }
      if (callClose && !closingRef.current.has(id)) {
        closingRef.current.add(id);
        found.onClose();
      }
      setOverlays(current => current.filter(overlay_0 => overlay_0.id !== id));
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const removeOverlay = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const found_0 = overlaysRef.current.at(-1);
      if (found_0) {
        removeOverlay(found_0.id);
      }
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const closeTopOverlay = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (id_0, render, t9, t10) => {
      const onClose = t9 === undefined ? _temp : t9;
      const clickToExit = t10 === undefined ? true : t10;
      closingRef.current.delete(id_0);
      setOverlays(current_0 => [...current_0.filter(overlay_1 => overlay_1.id !== id_0), {
        id: id_0,
        render,
        onClose,
        clickToExit
      }]);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const addOverlay = t8;
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const captureOrigin = event => {
        if (!(event.target instanceof HTMLElement)) {
          return;
        }
        const target = event.target.closest("[data-overlay-target]");
        if (!target) {
          setOpenFrom(undefined);
          return;
        }
        const rect = target.getBoundingClientRect();
        setOpenFrom({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        });
      };
      window.addEventListener("pointerdown", captureOrigin, true);
      return () => window.removeEventListener("pointerdown", captureOrigin, true);
    };
    t10 = [];
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[11] !== overlays.length) {
    t11 = () => {
      if (overlays.length === 0) {
        closingRef.current.clear();
        if (previousOverflowRef.current !== null) {
          document.documentElement.style.overflow = previousOverflowRef.current;
          previousOverflowRef.current = null;
        }
        return;
      }
      if (previousOverflowRef.current === null) {
        previousOverflowRef.current = document.documentElement.style.overflow;
      }
      document.documentElement.style.overflow = "hidden";
      const onKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          closeTopOverlay();
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t12 = [closeTopOverlay, overlays.length];
    $[11] = overlays.length;
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => () => {
      if (previousOverflowRef.current !== null) {
        document.documentElement.style.overflow = previousOverflowRef.current;
      }
    };
    t14 = [];
    $[14] = t13;
    $[15] = t14;
  } else {
    t13 = $[14];
    t14 = $[15];
  }
  useEffect(t13, t14);
  let t15;
  if ($[16] !== openFrom || $[17] !== overlays) {
    t15 = {
      overlays,
      openFrom,
      addOverlay,
      removeOverlay,
      closeTopOverlay
    };
    $[16] = openFrom;
    $[17] = overlays;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const value = t15;
  const t16 = overlays.length > 0;
  let t17;
  let t18;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_1 => {
      if (event_1.target instanceof HTMLElement && event_1.target.hasAttribute("data-overlay-bg")) {
        pressedBackdropRef.current = event_1.target;
      }
    };
    t18 = event_2 => {
      if (event_2.target instanceof HTMLElement && event_2.target.hasAttribute("data-overlay-bg") && event_2.target === pressedBackdropRef.current) {
        const found_1 = overlaysRef.current.at(-1);
        if (found_1?.clickToExit) {
          closeTopOverlay();
        }
      }
      pressedBackdropRef.current = null;
    };
    $[19] = t17;
    $[20] = t18;
  } else {
    t17 = $[19];
    t18 = $[20];
  }
  let t19;
  if ($[21] !== overlays) {
    t19 = overlays.map(_temp2);
    $[21] = overlays;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== t16 || $[24] !== t19) {
    t20 = <div id="overlay-controller" data-overlay-bg={true} data-overlay-controller={true} data-activated={t16} onPointerDown={t17} onPointerUp={t18} className="fixed inset-0 z-999 overflow-hidden bg-bg/75 opacity-0 backdrop-blur-md transition-[opacity,backdrop-filter] duration-300 ease-out data-[activated=false]:pointer-events-none data-[activated=true]:opacity-100">{t19}</div>;
    $[23] = t16;
    $[24] = t19;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== children || $[27] !== t20) {
    t21 = <div data-overlay-root={true} className="contents">{t20}{children}</div>;
    $[26] = children;
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== t21 || $[30] !== value) {
    t22 = <OverlayContext.Provider value={value}>{t21}</OverlayContext.Provider>;
    $[29] = t21;
    $[30] = value;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  return t22;
}
function _temp2(overlay_2) {
  return <span key={overlay_2.id} className="contents">{overlay_2.render}</span>;
}
function _temp() {}
export function useOverlay(content, onClose) {
  const $ = _c(5);
  const {
    addOverlay
  } = useOverlayContext();
  const id = useId();
  let t0;
  if ($[0] !== addOverlay || $[1] !== content || $[2] !== id || $[3] !== onClose) {
    t0 = (...t1) => {
      const args = t1;
      return addOverlay(id, <OverlaySub oid={id}><InternalOverlay overlayId={id}>{typeof content === "function" ? content(...args) : content}</InternalOverlay></OverlaySub>, onClose);
    };
    $[0] = addOverlay;
    $[1] = content;
    $[2] = id;
    $[3] = onClose;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}
export function useOverlayQuit() {
  return useContext(OverlaySubContext).quit;
}
export function useIsOverlay() {
  return !useContext(OverlaySubContext).invalid;
}
function InternalOverlay(t0) {
  const $ = _c(19);
  const {
    children,
    overlayId
  } = t0;
  const {
    closeTopOverlay,
    openFrom,
    overlays
  } = useOverlayContext();
  let t1;
  if ($[0] !== overlayId || $[1] !== overlays) {
    t1 = overlayId == null || overlays.at(-1)?.id === overlayId;
    $[0] = overlayId;
    $[1] = overlays;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isTop = t1;
  const transformOrigin = openFrom ? `${openFrom.left + openFrom.width / 2}px ${openFrom.top + openFrom.height / 2}px` : "50% 50%";
  const t2 = !isTop;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      scale: 0.985
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isTop ? 1 : 0;
  const t5 = isTop ? 1 : 0.99;
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = {
      opacity: t4,
      scale: t5
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      duration: 0.28,
      ease: [0.22, 1, 0.36, 1]
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== transformOrigin) {
    t8 = {
      transformOrigin
    };
    $[8] = transformOrigin;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== closeTopOverlay) {
    t9 = <CloseButton label="Close overlay" onClick={closeTopOverlay} className="right-4 top-4" />;
    $[10] = closeTopOverlay;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== children || $[13] !== isTop || $[14] !== t2 || $[15] !== t6 || $[16] !== t8 || $[17] !== t9) {
    t10 = <motion.div data-top={isTop} aria-hidden={t2} initial={t3} animate={t6} transition={t7} style={t8} className="fixed inset-0 isolate overflow-auto bg-bg text-text-primary data-[top=false]:pointer-events-none data-[top=false]:invisible">{t9}{children}</motion.div>;
    $[12] = children;
    $[13] = isTop;
    $[14] = t2;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function InternalModal(t0) {
  const $ = _c(23);
  const {
    children,
    style,
    overlayId
  } = t0;
  const {
    closeTopOverlay,
    openFrom,
    overlays
  } = useOverlayContext();
  let t1;
  if ($[0] !== overlayId || $[1] !== overlays) {
    t1 = overlayId == null || overlays.at(-1)?.id === overlayId;
    $[0] = overlayId;
    $[1] = overlays;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isTop = t1;
  const transformOrigin = openFrom ? `${openFrom.left + openFrom.width / 2}px ${openFrom.top + openFrom.height / 2}px` : "50% 50%";
  const t2 = !isTop;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      scale: 0.94,
      y: 14
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isTop ? 1 : 0;
  const t5 = isTop ? 1 : 0.98;
  const t6 = isTop ? 0 : 10;
  let t7;
  if ($[4] !== t4 || $[5] !== t5 || $[6] !== t6) {
    t7 = {
      opacity: t4,
      scale: t5,
      y: t6
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      duration: 0.28,
      ease: [0.22, 1, 0.36, 1]
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== style || $[10] !== transformOrigin) {
    t9 = {
      ...style,
      transformOrigin
    };
    $[9] = style;
    $[10] = transformOrigin;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== closeTopOverlay) {
    t10 = <CloseButton label="Close modal" onClick={closeTopOverlay} className="right-3 top-3 sm:right-4 sm:top-4" />;
    $[12] = closeTopOverlay;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== children || $[15] !== t10 || $[16] !== t2 || $[17] !== t7 || $[18] !== t9) {
    t11 = <motion.div role="dialog" aria-modal="true" aria-hidden={t2} initial={t3} animate={t7} transition={t8} style={t9} className="relative max-h-full overflow-hidden rounded-[1.75rem] border border-border bg-surface text-text-primary shadow-2xl">{t10}{children}</motion.div>;
    $[14] = children;
    $[15] = t10;
    $[16] = t2;
    $[17] = t7;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== isTop || $[21] !== t11) {
    t12 = <div data-valid={true} data-top={isTop} className="fixed inset-0 grid place-items-center p-3 data-[top=false]:pointer-events-none data-[top=false]:invisible sm:p-6">{t11}</div>;
    $[20] = isTop;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function CloseButton(t0) {
  const $ = _c(5);
  const {
    label,
    onClick,
    className
  } = t0;
  const t1 = `absolute z-30 grid size-9 place-items-center rounded-full border border-border bg-bg/80 text-text-primary/55 shadow-lg backdrop-blur-xl transition duration-200 hover:bg-bg hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-primary/20 ${className}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CloseIcon className="size-4" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== label || $[2] !== onClick || $[3] !== t1) {
    t3 = <button type="button" aria-label={label} onClick={onClick} className={t1}>{t2}</button>;
    $[1] = label;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function OverlaySub(t0) {
  const $ = _c(6);
  const {
    oid,
    children
  } = t0;
  const {
    removeOverlay
  } = useOverlayContext();
  let t1;
  if ($[0] !== oid || $[1] !== removeOverlay) {
    t1 = {
      quit: () => removeOverlay(oid)
    };
    $[0] = oid;
    $[1] = removeOverlay;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const value = t1;
  let t2;
  if ($[3] !== children || $[4] !== value) {
    t2 = <OverlaySubContext.Provider value={value}>{children}</OverlaySubContext.Provider>;
    $[3] = children;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function useNearestOverlayController() {
  const $ = _c(3);
  const [controller, setController] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      if (!node) {
        return;
      }
      const root = node.closest("[data-overlay-root]");
      setController(root?.querySelector("[data-overlay-controller]") ?? null);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const anchorRef = t0;
  let t1;
  if ($[1] !== controller) {
    t1 = {
      anchorRef,
      controller
    };
    $[1] = controller;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type IntrinsicTag = keyof JSX.IntrinsicElements;
type OverlayProps<T extends IntrinsicTag> = {
  children?: ReactNode | undefined;
  onClose?: (() => void) | undefined;
  clickToExit?: boolean | undefined;
} & Omit<ComponentPropsWithoutRef<T>, "children" | "onClose">;
type ModalProps<T extends IntrinsicTag> = {
  children?: ReactNode | undefined;
  onClose?: (() => void) | undefined;
  clickToExit?: boolean | undefined;
  modalStyle?: ModalSizeStyle | undefined;
} & Omit<ComponentPropsWithoutRef<T>, "children" | "onClose">;
type PortalElementProps = Record<string, unknown>;
type OverlayPortalProps = {
  tag: IntrinsicTag;
  elementProps: PortalElementProps;
  children?: ReactNode;
  onClose?: (() => void) | undefined;
  clickToExit: boolean;
};
type ModalPortalProps = OverlayPortalProps & {
  modalStyle: ModalSizeStyle;
};
function OverlayPortal(t0) {
  const $ = _c(29);
  const {
    tag,
    elementProps,
    children,
    onClose,
    clickToExit
  } = t0;
  const id = useId();
  const onCloseRef = useRef(onClose);
  const {
    anchorRef,
    controller
  } = useNearestOverlayController();
  const {
    addOverlay,
    removeOverlay,
    overlays
  } = useOverlayContext();
  let t1;
  let t2;
  if ($[0] !== onClose) {
    t1 = () => {
      onCloseRef.current = onClose;
    };
    t2 = [onClose];
    $[0] = onClose;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== addOverlay || $[4] !== clickToExit || $[5] !== id || $[6] !== removeOverlay) {
    t3 = () => {
      addOverlay(id, null, () => onCloseRef.current?.(), clickToExit);
      return () => removeOverlay(id, false);
    };
    t4 = [addOverlay, clickToExit, id, removeOverlay];
    $[3] = addOverlay;
    $[4] = clickToExit;
    $[5] = id;
    $[6] = removeOverlay;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== id || $[10] !== overlays) {
    let t6;
    if ($[12] !== id) {
      t6 = overlay => overlay.id === id;
      $[12] = id;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = overlays.some(t6);
    $[9] = id;
    $[10] = overlays;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const exists = t5;
  const Component = tag as ElementType;
  let t6;
  if ($[14] !== Component || $[15] !== children || $[16] !== elementProps) {
    t6 = createElement(Component, elementProps, children);
    $[14] = Component;
    $[15] = children;
    $[16] = elementProps;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const content = t6;
  let t7;
  if ($[18] !== anchorRef) {
    t7 = <span ref={anchorRef} hidden={true} />;
    $[18] = anchorRef;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== content || $[21] !== controller || $[22] !== exists || $[23] !== id || $[24] !== removeOverlay) {
    t8 = controller && exists && createPortal(<OverlaySubContext.Provider value={{
      quit: () => removeOverlay(id)
    }}><InternalOverlay overlayId={id}>{content}</InternalOverlay></OverlaySubContext.Provider>, controller);
    $[20] = content;
    $[21] = controller;
    $[22] = exists;
    $[23] = id;
    $[24] = removeOverlay;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t7 || $[27] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function ModalPortal(t0) {
  const $ = _c(30);
  const {
    tag,
    elementProps,
    children,
    onClose,
    clickToExit,
    modalStyle
  } = t0;
  const id = useId();
  const onCloseRef = useRef(onClose);
  const {
    anchorRef,
    controller
  } = useNearestOverlayController();
  const {
    addOverlay,
    removeOverlay,
    overlays
  } = useOverlayContext();
  let t1;
  let t2;
  if ($[0] !== onClose) {
    t1 = () => {
      onCloseRef.current = onClose;
    };
    t2 = [onClose];
    $[0] = onClose;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== addOverlay || $[4] !== clickToExit || $[5] !== id || $[6] !== removeOverlay) {
    t3 = () => {
      addOverlay(id, null, () => onCloseRef.current?.(), clickToExit);
      return () => removeOverlay(id, false);
    };
    t4 = [addOverlay, clickToExit, id, removeOverlay];
    $[3] = addOverlay;
    $[4] = clickToExit;
    $[5] = id;
    $[6] = removeOverlay;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== id || $[10] !== overlays) {
    let t6;
    if ($[12] !== id) {
      t6 = overlay => overlay.id === id;
      $[12] = id;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = overlays.some(t6);
    $[9] = id;
    $[10] = overlays;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const exists = t5;
  const Component = tag as ElementType;
  let t6;
  if ($[14] !== Component || $[15] !== children || $[16] !== elementProps) {
    t6 = createElement(Component, elementProps, children);
    $[14] = Component;
    $[15] = children;
    $[16] = elementProps;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const content = t6;
  let t7;
  if ($[18] !== anchorRef) {
    t7 = <span ref={anchorRef} hidden={true} />;
    $[18] = anchorRef;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== content || $[21] !== controller || $[22] !== exists || $[23] !== id || $[24] !== modalStyle || $[25] !== removeOverlay) {
    t8 = controller && exists && createPortal(<OverlaySubContext.Provider value={{
      quit: () => removeOverlay(id)
    }}><InternalModal overlayId={id} style={modalStyle}>{content}</InternalModal></OverlaySubContext.Provider>, controller);
    $[20] = content;
    $[21] = controller;
    $[22] = exists;
    $[23] = id;
    $[24] = modalStyle;
    $[25] = removeOverlay;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
type OverlayComponents = { [K in IntrinsicTag]: ComponentType<OverlayProps<K>> };
type ModalComponents = { [K in IntrinsicTag]: ComponentType<ModalProps<K>> };
function createOverlayComponent<T extends IntrinsicTag>(tag: T) {
  return function OverlayComponent({
    children,
    onClose,
    clickToExit = true,
    ...elementProps
  }: OverlayProps<T>) {
    return <OverlayPortal tag={tag} elementProps={elementProps as PortalElementProps} onClose={onClose} clickToExit={clickToExit}>{children}</OverlayPortal>;
  };
}
function createModalComponent<T extends IntrinsicTag>(tag: T) {
  return function ModalComponent({
    children,
    onClose,
    clickToExit = true,
    modalStyle = {
      maxWidth: "40svw",
      maxHeight: "80svh"
    },
    ...elementProps
  }: ModalProps<T>) {
    return <ModalPortal tag={tag} elementProps={elementProps as PortalElementProps} onClose={onClose} clickToExit={clickToExit} modalStyle={modalStyle}>{children}</ModalPortal>;
  };
}
const overlayComponents = new Map<IntrinsicTag, unknown>();
const modalComponents = new Map<IntrinsicTag, unknown>();
export const overlay = new Proxy({} as OverlayComponents, {
  get: (_target, property) => {
    if (typeof property !== "string") return undefined;
    const tag = property as IntrinsicTag;
    const cached = overlayComponents.get(tag);
    if (cached) return cached;
    const component = createOverlayComponent(tag);
    overlayComponents.set(tag, component);
    return component;
  }
});
export const modal = new Proxy({} as ModalComponents, {
  get: (_target, property) => {
    if (typeof property !== "string") return undefined;
    const tag = property as IntrinsicTag;
    const cached = modalComponents.get(tag);
    if (cached) return cached;
    const component = createModalComponent(tag);
    modalComponents.set(tag, component);
    return component;
  }
});